import { useRef, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

// Small inline mic button for dictating (Whisper) into any text field.
// onText(transcript) receives the transcribed text; the parent decides append vs replace.
export function DictationMic({ onText, disabled, className = "", title = "Dictate", size = "sm",
                              testId = "dictation-mic", endpoint = "/transcribe", post }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | working
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanup = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };
  useEffect(() => () => cleanup(), []);

  const transcribe = async () => {
    const blob = new Blob(chunksRef.current, { type: mediaRef.current?.mimeType || "audio/webm" });
    if (!blob.size) { setPhase("idle"); return; }
    const fd = new FormData();
    fd.append("file", blob, "dictation.webm");
    try {
      const r = post
        ? await post(endpoint, fd)
        : await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.text) onText(r.data.text);
      else toast.error("Didn't catch that — please try again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't transcribe that. Please try again.");
    } finally { setPhase("idle"); }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => transcribe();
      mr.start();
      setPhase("recording");
    } catch {
      toast.error("Microphone access was blocked. Please allow mic access and try again.");
    }
  };

  const stop = () => { try { mediaRef.current?.stop(); } catch { /* noop */ } cleanup(); setPhase("working"); };
  const toggle = () => { if (phase === "idle") start(); else if (phase === "recording") stop(); };

  const dim = size === "lg" ? "h-9 w-9" : "h-7 w-7";
  const icon = size === "lg" ? "h-4.5 w-4.5" : "h-4 w-4";

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={toggle}
      disabled={disabled || phase === "working"}
      title={phase === "recording" ? "Stop & transcribe" : title}
      aria-label={phase === "recording" ? "Stop and transcribe" : title}
      className={`shrink-0 inline-flex items-center justify-center ${dim} rounded-full transition-colors active:scale-95 disabled:opacity-40 disabled:pointer-events-none ${
        phase === "recording"
          ? "text-white bg-redline-500 hover:bg-redline-600 animate-pulse"
          : "text-ink-400 hover:text-redline-600 hover:bg-redline-50"
      } ${className}`}
    >
      {phase === "working" ? <Loader2 className={`${icon} animate-spin`} />
        : phase === "recording" ? <Square className="h-3 w-3" fill="currentColor" />
        : <Mic className={icon} strokeWidth={1.75} />}
    </button>
  );
}
